// ============================================================================
// SECURITY OVERVIEW COMPONENT
// ============================================================================
// Dashboard overview of security status matching AdminDashboard design system.
// ============================================================================

import React, { useEffect, useState } from 'react';
import {
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Spinner,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import {
  Shield,
  AlertTriangle,
  Users,
  RefreshCw,
  Flag,
  UserX,
  Bot,
  Coins,
  Wifi,
  Clock,
  CheckCircle,
  Activity,
} from 'lucide-react';
import { POKER_COLORS } from '../../../theme/designSystem';
import PokerCard from '../../ui/PokerCard';
import PokerButton from '../../ui/PokerButton';
import { securityFlagService, riskScoreService } from '../../../services/security';
import { SecurityFlag, PlayerRiskScore } from '../../../types/securityTypes';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const SecurityOverview: React.FC = () => {
  const [activeFlags, setActiveFlags] = useState<SecurityFlag[]>([]);
  const [recentFlags, setRecentFlags] = useState<SecurityFlag[]>([]);
  const [highRiskPlayers, setHighRiskPlayers] = useState<PlayerRiskScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const [flags, recent, highRisk] = await Promise.all([
        securityFlagService.getActiveFlags(),
        securityFlagService.getRecentFlags(10, 24 * 60 * 60 * 1000),
        riskScoreService.getHighRiskPlayers(10),
      ]);

      setActiveFlags(flags);
      setRecentFlags(recent);
      setHighRiskPlayers(highRisk);

    } catch (err) {
      console.error('Failed to load security stats:', err);
      setError('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  // ============================================================================
  // HELPERS
  // ============================================================================

  const formatTime = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getSeverityBadge = (severity: string) => {
    const colors: Record<string, string> = {
      critical: 'red',
      high: 'orange',
      medium: 'yellow',
      low: 'gray',
    };
    return <Badge colorScheme={colors[severity] || 'gray'}>{severity.toUpperCase()}</Badge>;
  };

  const getTypeBadge = (type: string) => {
    const labels: Record<string, string> = {
      collusion: 'Collusion',
      multi_account: 'Multi-Account',
      bot: 'Bot',
      chip_dumping: 'Chip Dump',
      same_ip_table: 'Same IP',
      suspicious_timing: 'Timing',
      unusual_pattern: 'Unusual',
    };
    return <Badge colorScheme="purple">{labels[type] || type}</Badge>;
  };

  const getRiskBadge = (level: string) => {
    const colors: Record<string, string> = {
      critical: 'red',
      high: 'orange',
      medium: 'yellow',
      low: 'green',
    };
    return <Badge colorScheme={colors[level] || 'gray'}>{level.toUpperCase()}</Badge>;
  };

  // Stats calculations
  const criticalCount = activeFlags.filter(f => f.severity === 'critical').length;
  const highCount = activeFlags.filter(f => f.severity === 'high').length;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const flagsToday = activeFlags.filter(f => 
    f.createdAt && f.createdAt.toDate() >= todayStart
  ).length;

  // By type counts
  const byType = {
    collusion: activeFlags.filter(f => f.type === 'collusion').length,
    multiAccount: activeFlags.filter(f => f.type === 'multi_account').length,
    bot: activeFlags.filter(f => f.type === 'bot').length,
    chipDumping: activeFlags.filter(f => f.type === 'chip_dumping').length,
    sameIp: activeFlags.filter(f => f.type === 'same_ip_table').length,
    timing: activeFlags.filter(f => f.type === 'suspicious_timing').length,
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <PokerCard>
        <VStack py={8}>
          <Spinner size="xl" color={POKER_COLORS.cyan} />
          <Text color="gray.400">Loading security data...</Text>
        </VStack>
      </PokerCard>
    );
  }

  if (error) {
    return (
      <PokerCard>
        <VStack py={8}>
          <AlertTriangle size={48} color="#E53E3E" />
          <Text color="red.400">{error}</Text>
          <PokerButton variant="secondary" size="sm" onClick={loadStats} leftIcon={<RefreshCw size={14} />}>
            Retry
          </PokerButton>
        </VStack>
      </PokerCard>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Header with refresh */}
      <PokerCard>
        <HStack justify="space-between">
          <HStack spacing={3}>
            <Shield size={24} color={POKER_COLORS.cyan} />
            <VStack align="start" spacing={0}>
              <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize="lg">Security Overview</Text>
              <Text color="gray.500" fontSize="sm">Updated just now</Text>
            </VStack>
          </HStack>
          <PokerButton variant="secondary" size="sm" leftIcon={<RefreshCw size={14} />} onClick={loadStats}>
            Refresh
          </PokerButton>
        </HStack>
      </PokerCard>

      {/* Primary Stats */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <PokerCard p={4} borderColor={criticalCount > 0 ? 'red.500' : undefined} borderWidth={criticalCount > 0 ? 2 : 0}>
          <Stat>
            <StatLabel color="gray.400"><HStack><AlertTriangle size={14} /><Text>Active Flags</Text></HStack></StatLabel>
            <StatNumber color={activeFlags.length > 0 ? 'red.400' : POKER_COLORS.cyan}>{activeFlags.length}</StatNumber>
            <StatHelpText color={criticalCount > 0 ? 'red.400' : 'gray.500'}>{criticalCount} critical</StatHelpText>
          </Stat>
        </PokerCard>

        <PokerCard p={4}>
          <Stat>
            <StatLabel color="gray.400"><HStack><Users size={14} /><Text>High Risk Players</Text></HStack></StatLabel>
            <StatNumber color={highRiskPlayers.length > 0 ? 'orange.400' : POKER_COLORS.darkGreen}>{highRiskPlayers.length}</StatNumber>
            <StatHelpText color="gray.500">{highRiskPlayers.filter(p => p.riskLevel === 'critical').length} critical</StatHelpText>
          </Stat>
        </PokerCard>

        <PokerCard p={4}>
          <Stat>
            <StatLabel color="gray.400"><HStack><Activity size={14} /><Text>Flags Today</Text></HStack></StatLabel>
            <StatNumber color={POKER_COLORS.gold}>{flagsToday}</StatNumber>
            <StatHelpText color="gray.500">Last 24 hours</StatHelpText>
          </Stat>
        </PokerCard>

        <PokerCard p={4}>
          <Stat>
            <StatLabel color="gray.400"><HStack><Clock size={14} /><Text>Pending Review</Text></HStack></StatLabel>
            <StatNumber color={POKER_COLORS.cyan}>{activeFlags.filter(f => f.status === 'reviewing').length}</StatNumber>
            <StatHelpText color="gray.500">Awaiting action</StatHelpText>
          </Stat>
        </PokerCard>
      </SimpleGrid>

      {/* Flags by Type */}
      <SimpleGrid columns={{ base: 3, md: 6 }} spacing={4}>
        <PokerCard p={4}>
          <HStack spacing={2} mb={1}>
            <Users size={14} color="#9F7AEA" />
            <Text color="gray.500" fontSize="sm">Collusion</Text>
          </HStack>
          <Text color="purple.400" fontSize="xl" fontWeight="bold">{byType.collusion}</Text>
        </PokerCard>
        <PokerCard p={4}>
          <HStack spacing={2} mb={1}>
            <UserX size={14} color="#E53E3E" />
            <Text color="gray.500" fontSize="sm">Multi-Account</Text>
          </HStack>
          <Text color="red.400" fontSize="xl" fontWeight="bold">{byType.multiAccount}</Text>
        </PokerCard>
        <PokerCard p={4}>
          <HStack spacing={2} mb={1}>
            <Bot size={14} color="#DD6B20" />
            <Text color="gray.500" fontSize="sm">Bot</Text>
          </HStack>
          <Text color="orange.400" fontSize="xl" fontWeight="bold">{byType.bot}</Text>
        </PokerCard>
        <PokerCard p={4}>
          <HStack spacing={2} mb={1}>
            <Coins size={14} color="#D69E2E" />
            <Text color="gray.500" fontSize="sm">Chip Dump</Text>
          </HStack>
          <Text color="yellow.400" fontSize="xl" fontWeight="bold">{byType.chipDumping}</Text>
        </PokerCard>
        <PokerCard p={4}>
          <HStack spacing={2} mb={1}>
            <Wifi size={14} color="#38A169" />
            <Text color="gray.500" fontSize="sm">Same IP</Text>
          </HStack>
          <Text color="green.400" fontSize="xl" fontWeight="bold">{byType.sameIp}</Text>
        </PokerCard>
        <PokerCard p={4}>
          <HStack spacing={2} mb={1}>
            <Clock size={14} color="#3182CE" />
            <Text color="gray.500" fontSize="sm">Timing</Text>
          </HStack>
          <Text color="blue.400" fontSize="xl" fontWeight="bold">{byType.timing}</Text>
        </PokerCard>
      </SimpleGrid>

      {/* Recent Flags Table */}
      <PokerCard>
        <HStack color={POKER_COLORS.cyan} mb={4}>
          <Flag size={16} />
          <Text fontWeight="bold">Recent Flags</Text>
        </HStack>
        
        {recentFlags.length === 0 ? (
          <VStack py={8}>
            <CheckCircle size={48} color="#38A169" />
            <Text color="gray.500">No recent flags - all clear!</Text>
          </VStack>
        ) : (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th color="gray.500">Type</Th>
                  <Th color="gray.500">Severity</Th>
                  <Th color="gray.500">Player</Th>
                  <Th color="gray.500">Confidence</Th>
                  <Th color="gray.500">Time</Th>
                </Tr>
              </Thead>
              <Tbody>
                {recentFlags.slice(0, 5).map(flag => (
                  <Tr key={flag.id}>
                    <Td>{getTypeBadge(flag.type)}</Td>
                    <Td>{getSeverityBadge(flag.severity)}</Td>
                    <Td color="white">{flag.primaryUserId?.substring(0, 12)}...</Td>
                    <Td color={POKER_COLORS.gold}>{flag.confidence}%</Td>
                    <Td color="gray.400">{formatTime(flag.createdAt)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </PokerCard>

      {/* High Risk Players */}
      <PokerCard>
        <HStack color="orange.400" mb={4}>
          <Users size={16} />
          <Text fontWeight="bold">High Risk Players</Text>
        </HStack>
        
        {highRiskPlayers.length === 0 ? (
          <VStack py={8}>
            <CheckCircle size={48} color="#38A169" />
            <Text color="gray.500">No high-risk players detected</Text>
          </VStack>
        ) : (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th color="gray.500">Player ID</Th>
                  <Th color="gray.500">Risk Level</Th>
                  <Th color="gray.500">Overall Score</Th>
                  <Th color="gray.500">Active Flags</Th>
                  <Th color="gray.500">Last Updated</Th>
                </Tr>
              </Thead>
              <Tbody>
                {highRiskPlayers.slice(0, 5).map(player => (
                  <Tr key={player.odD}>
                    <Td color="white">{player.odD?.substring(0, 12)}...</Td>
                    <Td>{getRiskBadge(player.riskLevel)}</Td>
                    <Td color={POKER_COLORS.gold}>{player.overallRisk}%</Td>
                    <Td color="red.400">{player.activeFlags}</Td>
                    <Td color="gray.400">{formatTime(player.lastUpdated)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </PokerCard>
    </VStack>
  );
};

export default SecurityOverview;